import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getClientEmployeeMenuItems } from 'utils/config/clientEmployeeMenuItems';
import { getClientAdminMenuItems } from 'utils/config/clientAdminMenuItems';
import operatorClientMenuItems from 'utils/config/operatorClientMenuItems';
import { buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { PermissionKeys } from 'store/types';
import { usePermissionsContext } from 'store/permissions';
import { getUserInfo } from 'utils/auth';
import HeaderBase from './HeaderBase';

const HeaderClient: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { permissions, checkPermission } = usePermissionsContext();
  const [isClientAdmin, setIsClientAdmin] = useState(checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV));
  const fromOperator = client?.id;
  const userInfo = getUserInfo();
  const isLeavesRoute = history.location.pathname.split('/')[1] === 'my-leaves';

  useEffect(() => {
    setIsClientAdmin(checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV));
  }, [permissions]);

  // Build the correct urls with client ID
  const operatorClientMenu = operatorClientMenuItems.map(item => {
    if (item.type === 'exit-client') return item;
    const to = buildClientUrl(item.to, client);
    return {
      ...item,
      to,
      ...(item.childrenRoutes?.length && {
        childrenRoutes: item.childrenRoutes.map(route => ({
          ...route,
          to: buildClientUrl(route.to, client),
        })),
      }),
    };
  });
  const clientMenu = isClientAdmin
    ? getClientAdminMenuItems({
        /**
         * Moderators/Employers should have the permission to manage leaves to see it in the menu
         * but only if the tenant has the feature ON
         */
        shouldSeeLeaves: userInfo.featureLeavesIsActive && checkPermission(PermissionKeys.CAN_MANAGE_LEAVES),
        shouldSeeHourlyAbsences:
          userInfo.featureHourlyAbsences && checkPermission(PermissionKeys.CAN_MANAGE_HOURLY_ABSENCES),
        shouldSeeHealthAndSafetyTraining:
          userInfo.featureHealthAndSafetyTraining &&
          checkPermission(PermissionKeys.CAN_MANAGE_HEALTH_AND_SAFETY_TRAINING),
        shouldSeeWorkTrips: userInfo.featureWorkTrips && checkPermission(PermissionKeys.CAN_MANAGE_WORK_TRIPS),
      })
    : getClientEmployeeMenuItems({
        /**
         * For employees we check if they have this feature ON and if Yes - they should see it
         */
        shouldSeeLeaves: userInfo.featureLeavesIsActive,
        isLeavesRoute,
        shouldSeeColleaguesLeaves: userInfo.featureColleaguesLeaves,
        shouldSeeHourlyAbsences: userInfo.featureHourlyAbsences,
        shouldSeeWorkTrips: userInfo.featureWorkTrips,
        t: (str: string) => t(str),
      });
  const menuItems = fromOperator ? operatorClientMenu : clientMenu;
  return (
    <HeaderBase
      menuItems={menuItems}
      showLangSwitch={!fromOperator}
      isClientAdmin={isClientAdmin}
    />
  );
};

export default HeaderClient;
